import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Share2, Zap, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { AddonDialog } from "@/components/social/AddonDialog";
import { useSuperRawaj } from "@/components/social/SuperRawajPanel";
import { AccountCards } from "@/components/social/AccountCards";
import { NotifyEmail } from "@/components/social/NotifyEmail";
import { PublishMode } from "@/components/social/PublishMode";
import { GrowthCharts } from "@/components/social/GrowthCharts";
import { Competitors } from "@/components/social/Competitors";
import { ExecSummary } from "@/components/social/ExecSummary";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";

/** الأدمن فقط: كل الإضافات بما فيها الموقوفة عن العملاء — للتجربة قبل الإطلاق */
function AdminAddonsPreview() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/addons").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.some((a) => a.admin_preview || a.admin)) return null;
  return (
    <div className="glass rounded-2xl p-4 border border-accent/40 bg-accent/5 space-y-2" data-testid="admin-addons-preview">
      <div className="text-sm font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-accent" /> وضع تجربة الأدمن — كل الإضافات مفعّلة لك هنا حتى الموقوفة عن العملاء</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {list.map((a) => (
          <div key={a.id} className="rounded-xl border border-border bg-background/60 p-3 text-xs" data-testid={`admin-addon-${a.id}`}>
            <div className="font-bold text-sm flex items-center justify-between gap-2">{a.name}
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{a.enabled ? "متاحة للعملاء" : "موقوفة عن العملاء — تجربة الأدمن"}</span>
            </div>
            <p className="text-muted-foreground mt-1 leading-relaxed">{a.description}</p>
            {a.id === "exec_secretary" && <p className="mt-1 text-amber-800 dark:text-amber-300">الردود الذكية على رسائل العملاء تحتاج ربط صندوق الرسائل (إنستغرام) — جاهزة للبناء كمرحلة قادمة.</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SocialHub() {
  const { activeProject } = useProjects();
  const { addon, loaded, reload } = useSuperRawaj();
  const { t } = useLang();
  const [dialog, setDialog] = useState(false);
  const [range, setRange] = useState("week");
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (params.get("connected")) { toast.success(t("تم ربط الحساب بنجاح ✅", "Account connected successfully ✅")); setParams({}, { replace: true }); }
    else if (params.get("error")) {
      const r = params.get("reason");
      const msg = { denied: "رفضت المنصة الطلب أو أُلغيت الصلاحيات — أعد المحاولة ووافق على الصلاحيات الثلاث",
        expired: "انتهت صلاحية جلسة الربط (30 دقيقة) أو أُعيد استخدام رابط قديم — ابدأ الربط من جديد بالضغط على «ربط الحساب»",
        exchange: "تعذّر إتمام الربط مع المنصة — تأكد أن الحساب مهني (Business/Creator) وأنك سجّلت دخولك به، ثم أعد المحاولة",
        tester: "إنستغرام منحت الصلاحية لكنها رفضت إصدار صلاحية طويلة الأجل لهذا الحساب — حتى يكتمل اعتماد Meta لتطبيق رواج الذكية، يلزم إضافة الحساب كمختبر (Instagram Tester) في التطبيق وقبول الدعوة من إعدادات إنستغرام (Apps and websites → Tester invites) ثم إعادة الربط. تواصل مع الدعم لإضافته." }[r] || "تعذّر ربط الحساب — حاول مرة أخرى";
      const d = params.get("detail");
      toast.error(d ? `${msg} (${d})` : msg, { duration: r === "tester" ? 20000 : 9000 }); setParams({}, { replace: true });
    }
  }, [params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!addon && !loaded) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!addon) return (
    <div className="glass rounded-3xl p-10 text-center space-y-3 border border-border" data-testid="social-hub-unavailable">
      <Share2 className="w-10 h-10 text-muted-foreground mx-auto" />
      <h1 className="text-2xl font-extrabold font-heading">{t("قنوات التواصل", "Social Channels")}</h1>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">{t("إضافة «سوبر رواج» متوقفة حاليًا عن العملاء — ستظهر هنا فور إطلاقها. يمكنك الاستفادة من واتساب بوت وإيميل بوت الآن.", "The Super Rawaj add-on is currently unavailable for customers — it will appear here once launched. WhatsApp Bot and Email Bot are available now.")}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="social-hub-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold font-heading flex items-center gap-2"><Share2 className="w-7 h-7 text-primary" /> {t("قنوات التواصل", "Social Channels")}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{t("اربط حساباتك رسمياً، تابع نموك يوماً وأسبوعاً وشهراً وسنة، وقارن موقعك بالمنافسين بملخص تنفيذي ذكي.", "Connect your accounts via official login, track your growth by day, week, month and year, and compare with competitors through a smart executive summary.")}</p>
        </div>
        {addon.active && addon.expires_at && <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="addon-active-badge"><CheckCircle2 className="w-3.5 h-3.5" /> {t("سوبر رواج مفعّل حتى", "Super Rawaj active until")} {String(addon.expires_at).slice(0, 10)}</span>}
      </div>

      {!addon.active ? (
        <div className="glass rounded-3xl p-8 border border-accent/40 bg-accent/5 grid lg:grid-cols-5 gap-6 items-center" data-testid="super-rawaj-upsell">
          <div className="lg:col-span-3 space-y-4">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-accent bg-accent/15 px-3 py-1 rounded-full"><Zap className="w-3.5 h-3.5" /> إضافة اختيارية خارج الباقات</div>
            <h2 className="text-2xl font-extrabold font-heading">{addon.name}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{addon.description}</p>
            <ul className="space-y-2 text-sm">
              {(addon.features || []).map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}</li>)}
            </ul>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> الربط عبر OAuth الرسمي لكل منصة — لا نطلب كلمات المرور ولا نخزّنها.</p>
          </div>
          <div className="lg:col-span-2 glass rounded-2xl p-6 text-center border border-border">
            <div className="text-xs text-muted-foreground">قيمة الإضافة</div>
            <div className="text-5xl font-extrabold text-primary my-2" data-testid="super-rawaj-price">{addon.price}<span className="text-base mr-1">ريال</span></div>
            <div className="text-xs text-muted-foreground mb-5">كل {addon.period_days || 30} يوماً · تُضاف فوق باقتك الحالية · إلغاء في أي وقت</div>
            <Button className="w-full font-bold gap-2 bg-accent hover:bg-accent/90 text-accent-foreground" onClick={() => setDialog(true)} data-testid="super-rawaj-activate-btn"><Zap className="w-4 h-4" /> فعّل سوبر رواج</Button>
          </div>
          {dialog && <AddonDialog addon={addon} onClose={() => setDialog(false)} onActivated={reload} />}
        </div>
      ) : (
        <>
          {addon.admin && <AdminAddonsPreview />}
          <AccountCards />
          <PublishMode addon={addon} onChanged={reload} />
          <NotifyEmail />
          <GrowthCharts projectId={activeProject?.id} onRange={setRange} />
          <Competitors projectId={activeProject?.id} />
          <ExecSummary projectId={activeProject?.id} range={range} />
        </>
      )}
    </div>
  );
}
